// Phase 83y.31 — Pickleague-themed confirm dialog (replacement for
// window.confirm). Single shared component + imperative `openConfirm(...)`
// helper backed by a global event so any code path — including non-React
// callsites — can show a Pickleague-styled confirmation without dragging
// the browser's grey iOS alert into the experience.
//
// Usage:
//   import { openConfirm } from "@/components/ui/ConfirmDialog";
//   const ok = await openConfirm({
//     title: "Đăng xuất tài khoản?",
//     description: "Bạn chắc chắn muốn đăng xuất khỏi PickLeague?",
//     confirmText: "Đăng xuất",
//     cancelText: "Huỷ",
//     tone: "danger",      // danger | warning | default
//     icon: "logout",      // logout | warning | trash | shield | info
//   });
//   if (!ok) return;
//
// Mount once near the app root:  <ConfirmDialogProvider />
import { useEffect, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  SignOut,
  Warning,
  Trash,
  ShieldCheck,
  Info,
  X,
} from "@phosphor-icons/react";

const ICONS = {
  logout: SignOut,
  warning: Warning,
  trash: Trash,
  shield: ShieldCheck,
  info: Info,
};

const TONE_STYLES = {
  default: {
    iconBg: "linear-gradient(135deg, rgba(255,138,0,0.22), rgba(255,138,0,0.05))",
    iconBorder: "rgba(255,138,0,0.5)",
    iconColor: "#FF8A00",
    confirmBg: "linear-gradient(90deg, #FF8A00 0%, #FF9F1C 100%)",
    confirmShadow: "0 10px 28px rgba(255,138,0,0.45)",
  },
  warning: {
    iconBg: "linear-gradient(135deg, rgba(255,138,0,0.22), rgba(255,138,0,0.05))",
    iconBorder: "rgba(255,138,0,0.5)",
    iconColor: "#FF8A00",
    confirmBg: "linear-gradient(90deg, #FF8A00 0%, #FF9F1C 100%)",
    confirmShadow: "0 10px 28px rgba(255,138,0,0.45)",
  },
  danger: {
    iconBg: "linear-gradient(135deg, rgba(255,77,79,0.22), rgba(255,77,79,0.05))",
    iconBorder: "rgba(255,77,79,0.55)",
    iconColor: "#FF4D4F",
    confirmBg: "linear-gradient(90deg, #FF4D4F 0%, #FF6B6B 100%)",
    confirmShadow: "0 10px 28px rgba(255,77,79,0.45)",
  },
};

// Imperative API.
let _resolver = null;
let _setOptions = null;

export function openConfirm(opts) {
  return new Promise((resolve) => {
    _resolver = resolve;
    _setOptions?.({
      title: "Xác nhận",
      description: "",
      confirmText: "Đồng ý",
      cancelText: "Huỷ",
      tone: "default",
      icon: "info",
      mode: "confirm",
      ...opts,
    });
  });
}

/**
 * Phase 83y.32 — openAlert: 1-button informational/error dialog,
 * cùng theme với openConfirm. Dùng cho cảnh báo quan trọng mà toast
 * không đủ "đập vào mắt" — VD: thanh toán thất bại, mất kết nối, lỗi
 * tải hồ sơ, v.v.
 *
 * Usage:
 *   await openAlert({
 *     title: "Không tải được hồ sơ",
 *     description: "Vui lòng kiểm tra mạng và thử lại.",
 *     tone: "danger",
 *     icon: "warning",
 *     confirmText: "Đã hiểu",
 *   });
 */
export function openAlert(opts) {
  return new Promise((resolve) => {
    _resolver = resolve;
    _setOptions?.({
      title: "Thông báo",
      description: "",
      confirmText: "Đã hiểu",
      tone: "default",
      icon: "info",
      ...opts,
      mode: "alert",
    });
  });
}

/**
 * Phase 83y.33 — openPrompt: dialog với 1 ô input để user nhập text
 * (replacement for native `window.prompt`). Returns `Promise<string|null>`
 * — null khi user huỷ, string đã trim khi user xác nhận.
 *
 * Usage:
 *   const note = await openPrompt({
 *     title: "Ghi chú đối soát",
 *     description: "Tuỳ chọn — thêm context khi cần.",
 *     placeholder: "VD: chuyển khoản chậm 1 ngày",
 *     defaultValue: "",
 *     confirmText: "Lưu",
 *     required: false,
 *     multiline: false,   // true → render textarea 3 rows
 *     icon: "info",
 *     tone: "default",
 *   });
 *   if (note === null) return; // user pressed Huỷ
 */
export function openPrompt(opts) {
  return new Promise((resolve) => {
    _resolver = (val) => resolve(val);
    _setOptions?.({
      title: "Nhập thông tin",
      description: "",
      placeholder: "Nhập tại đây…",
      defaultValue: "",
      confirmText: "Xác nhận",
      cancelText: "Huỷ",
      tone: "default",
      icon: "info",
      required: false,
      multiline: false,
      maxLength: 500,
      ...opts,
      mode: "prompt",
    });
  });
}

export function ConfirmDialogProvider() {
  const [options, setOptions] = useState(null);
  // Phase 83y.33 — input state for prompt mode. Reset whenever a new
  // prompt opens; ignored in confirm/alert modes.
  const [inputValue, setInputValue] = useState("");
  useEffect(() => {
    _setOptions = (opts) => {
      setOptions(opts);
      if (opts?.mode === "prompt") {
        setInputValue(opts.defaultValue || "");
      }
    };
    return () => {
      _setOptions = null;
    };
  }, []);

  const close = (result) => {
    setOptions(null);
    setInputValue("");
    if (_resolver) {
      _resolver(result);
      _resolver = null;
    }
  };

  // Prompt-specific submit — returns trimmed string (or null on cancel).
  const submitPrompt = () => {
    const val = inputValue.trim();
    if (options?.required && val.length === 0) return; // blocked
    close(val);
  };
  const cancelPrompt = () => close(null);

  const open = Boolean(options);
  const tone = TONE_STYLES[options?.tone] || TONE_STYLES.default;
  const Icon = ICONS[options?.icon] || ICONS.info;
  const isPrompt = options?.mode === "prompt";
  const isAlert = options?.mode === "alert";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/72 backdrop-blur-md p-5"
          onClick={() => (isPrompt ? cancelPrompt() : close(false))}
          data-testid="confirm-dialog"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[400px] rounded-3xl p-6 relative"
            style={{
              background: "linear-gradient(180deg, #0B1220 0%, #060B14 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow:
                "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.02) inset",
            }}
          >
            <button
              type="button"
              onClick={() => (isPrompt ? cancelPrompt() : close(false))}
              aria-label="Đóng"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/[0.04] border border-white/8 flex items-center justify-center hover:bg-white/[0.08] transition"
              data-testid="confirm-dialog-close"
            >
              <X size={14} weight="bold" className="text-white/65" />
            </button>

            <div className={`flex flex-col ${isPrompt ? "items-start text-left" : "items-center text-center"}`}>
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{
                  background: tone.iconBg,
                  border: `1px solid ${tone.iconBorder}`,
                }}
              >
                <Icon size={28} weight="duotone" style={{ color: tone.iconColor }} />
              </div>
              <h3
                className="text-[18px] font-black text-white leading-tight tracking-tight"
                data-testid="confirm-dialog-title"
              >
                {options?.title}
              </h3>
              {options?.description && (
                <p
                  className="mt-2 text-[13px] text-white/65 leading-relaxed"
                  data-testid="confirm-dialog-description"
                >
                  {options.description}
                </p>
              )}
              {isPrompt && (
                <div className="w-full mt-4">
                  {options.multiline ? (
                    <textarea
                      autoFocus
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={options.placeholder}
                      maxLength={options.maxLength}
                      rows={3}
                      className="w-full px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 focus:border-[#FF8A00]/60 focus:outline-none text-[14px] text-white placeholder:text-white/30 resize-none transition"
                      data-testid="confirm-dialog-input"
                    />
                  ) : (
                    <input
                      autoFocus
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          submitPrompt();
                        }
                      }}
                      placeholder={options.placeholder}
                      maxLength={options.maxLength}
                      className="w-full h-12 px-4 rounded-2xl bg-white/[0.04] border border-white/10 focus:border-[#FF8A00]/60 focus:outline-none text-[14px] text-white placeholder:text-white/30 transition"
                      data-testid="confirm-dialog-input"
                    />
                  )}
                  {options.required && inputValue.trim().length === 0 && (
                    <div className="mt-1.5 text-[11px] text-white/40">
                      Vui lòng nhập nội dung để tiếp tục.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              {!isAlert && (
                <button
                  type="button"
                  onClick={() => (isPrompt ? cancelPrompt() : close(false))}
                  className="flex-1 h-12 rounded-2xl bg-white/[0.04] border border-white/10 text-white/80 text-[13.5px] font-bold hover:bg-white/[0.08] active:scale-[0.98] transition"
                  data-testid="confirm-dialog-cancel"
                >
                  {options?.cancelText || "Huỷ"}
                </button>
              )}
              <button
                type="button"
                onClick={() => (isPrompt ? submitPrompt() : close(true))}
                disabled={isPrompt && options?.required && inputValue.trim().length === 0}
                className={`${isAlert ? "w-full" : "flex-1"} h-12 rounded-2xl text-white text-[13.5px] font-black active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed`}
                style={{
                  background: tone.confirmBg,
                  boxShadow: tone.confirmShadow,
                }}
                data-testid="confirm-dialog-confirm"
              >
                {options?.confirmText || (isAlert ? "Đã hiểu" : "Đồng ý")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ConfirmDialogProvider;
